import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "gsintegrations";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { setupAuthInterceptor } from "./api";
import AppToaster from "./components/AppToaster.tsx";
import { queryClient } from "./components/queryClient.ts";
import hydratePersistedCache from "./hydratePersistedCache.tsx";
import PostHogAnalyticsProvider from "./components/PostHogAnalyticsProvider.tsx";
import { AppRouter } from "./routes/AppRouter.tsx";

// Lazy load PWAUpdatePrompt to avoid loading virtual:pwa-register before React is ready
// Wrapped in error boundary to prevent SW registration issues from breaking the app
const PWAUpdatePrompt = lazy(() =>
  import("./components/PWAUpdatePrompt.tsx")
    .then((module) => ({
      default: module.PWAUpdatePrompt,
    }))
    .catch((error) => {
      console.error("Failed to load PWAUpdatePrompt:", error);
      // Return a no-op component on error
      return { default: () => null };
    }),
);

const ReactQueryDevtools =
  import.meta.env.DEV && import.meta.env.VITE_SHOW_QUERY_DEVTOOLS === "true"
    ? lazy(() =>
        import("@tanstack/react-query-devtools").then((module) => ({
          default: module.ReactQueryDevtools,
        })),
      )
    : null;

setupAuthInterceptor();
// hydrate persisted cache before render
hydratePersistedCache(queryClient);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PostHogAnalyticsProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AppRouter />
          {ReactQueryDevtools ? (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          ) : null}
          <Suspense fallback={null}>
            <PWAUpdatePrompt />
          </Suspense>
          <AppToaster />
        </ThemeProvider>
      </QueryClientProvider>
    </PostHogAnalyticsProvider>
  </StrictMode>,
);
